import React, { useState } from 'react';
import type { Skill } from '../types.ts';
import { useSkillsManager } from '../hooks/useSkillsManager.ts';

interface SkillsManagerUIProps {
  backendUrl: string;
}

export function SkillsManagerUI({ backendUrl }: SkillsManagerUIProps) {
  const {
    skills,
    isLoading,
    error,
    toggleSkill,
    addSkill,
    updateSkillContent,
    deleteSkill
  } = useSkillsManager(backendUrl);

  const [activeSkill, setActiveSkill] = useState<Skill | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formContent, setFormContent] = useState('');

  const handleOpenEdit = (skill: Skill) => {
    setActiveSkill(skill);
    setFormName(skill.name);
    setFormDescription(skill.description);
    setFormContent(skill.bodyContent || skill.content);
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleOpenCreate = () => {
    setActiveSkill(null);
    setFormName('');
    setFormDescription('');
    setFormContent('1. Napište sem konkrétní pravidla pro rozvržení nábytku...\n2. Další pravidlo...');
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formContent.trim()) return;

    if (isCreating) {
      await addSkill({
        name: formName,
        description: formDescription,
        content: formContent
      });
      setIsCreating(false);
    } else if (isEditing && activeSkill) {
      await updateSkillContent(activeSkill.id, {
        name: formName,
        description: formDescription,
        content: formContent
      });
      setIsEditing(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Opravdu chcete smazat tento skill a jeho .md soubor?')) {
      await deleteSkill(id);
      if (activeSkill?.id === id) {
        setActiveSkill(null);
        setIsEditing(false);
      }
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#f3f4f6' }}>
            🧠 Knihovna Klientských Skillů (Agent Skills)
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#9ca3af' }}>
            Spravujte pravidla a znalostní bázi pro AI architekta. Každý skill je uložen jako <code>/skills/*.md</code> soubor.
          </p>
        </div>

        <div>
          <button onClick={handleOpenCreate} style={createBtnStyle}>
            ➕ Vytvořit Nový Skill
          </button>
        </div>
      </div>

      {error && (
        <div style={errorAlertStyle}>
          Chyba: {error}
        </div>
      )}

      {/* Main Content Grid */}
      <div style={gridStyle}>
        {/* Skills List Column */}
        <div style={skillsListColumnStyle}>
          <h3 style={sectionHeaderStyle}>
            Aktivní a Dostupné Skilly ({skills.length})
          </h3>

          {isLoading ? (
            <div style={{ color: '#9ca3af', padding: '20px', fontSize: '14px' }}>
              Načítání skillů...
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {skills.map(skill => (
                <div
                  key={skill.id}
                  style={skillCardStyle(skill.enabled)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1, paddingRight: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '15px', fontWeight: 600, color: '#f3f4f6' }}>
                          {skill.name}
                        </span>
                        <span style={badgeStyle(skill.enabled)}>
                          {skill.enabled ? 'Aktivní' : 'Vypnuto'}
                        </span>
                      </div>
                      <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#9ca3af', lineHeight: '1.4' }}>
                        {skill.description}
                      </p>
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }}>
                        📄 {skill.filePath ? skill.filePath.split('/').pop() : `${skill.id}.md`}
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                      <label style={toggleLabelStyle}>
                        <input
                          type="checkbox"
                          checked={skill.enabled}
                          onChange={(e) => toggleSkill(skill.id, e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '12px', color: skill.enabled ? '#ab47bc' : '#9ca3af' }}>
                          {skill.enabled ? 'Zapnuto' : 'Vypnuto'}
                        </span>
                      </label>

                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleOpenEdit(skill)}
                          style={smallActionBtnStyle}
                        >
                          ✏️ Upravit
                        </button>
                        <button
                          onClick={() => handleDelete(skill.id)}
                          style={smallDeleteBtnStyle}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor / Details Column */}
        <div style={editorColumnStyle}>
          {(isEditing || isCreating) ? (
            <div style={editorCardStyle}>
              <h3 style={sectionHeaderStyle}>
                {isCreating ? '➕ Vytvoření Nového Skillu' : `✏️ Úprava Skillu: ${activeSkill?.name}`}
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={fieldLabelStyle}>
                  Název Skillu:
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="např. Ergonomie Nábytku a Vzdáleností"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={fieldLabelStyle}>
                  Stručný Popis (Description):
                </label>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
                  💡 Stručné shrnutí pravidel. Tento popis se posílá do 1. fáze (Discovery) pro rozhodnutí, zda skill použít.
                </div>
                <input
                  type="text"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="např. Pravidla pro odstup stolku od pohovky a vzdálenost TV"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={fieldLabelStyle}>
                  Obsah Pravidel (Markdown Content):
                </label>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
                  💡 Plné znění pravidel a parametrů v Markdown formátu. Načte se a pošle do systému ve 2. fázi pro generování odpovědi (i s nadpisem a popisem).
                </div>
                <textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  rows={14}
                  style={textareaStyle}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setIsCreating(false);
                  }}
                  style={cancelBtnStyle}
                >
                  Zrušit
                </button>
                <button onClick={handleSave} style={saveBtnStyle}>
                  💾 Uložit Skill
                </button>
              </div>
            </div>
          ) : (
            <div style={placeholderCardStyle}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📖</div>
              <h4 style={{ margin: 0, fontSize: '16px', color: '#f3f4f6' }}>
                Vyberte skill k úpravě nebo vytvořte nový
              </h4>
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#9ca3af' }}>
                Skilly představují klientská pravidla a principy architektury. Při spuštění chatu se automaticky připojí do systémových instrukcí pro Gemini AI.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------- Styles -----------------------------------

const containerStyle: React.CSSProperties = {
  backgroundColor: '#1e1e1e',
  border: '1px solid #333333',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #333333',
  paddingBottom: '16px',
  marginBottom: '20px'
};

const createBtnStyle: React.CSSProperties = {
  backgroundColor: '#9c27b0',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '10px 16px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer'
};



const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px'
};

const skillsListColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column'
};

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#f3f4f6',
  marginTop: 0,
  marginBottom: '14px'
};

const skillCardStyle = (enabled: boolean): React.CSSProperties => ({
  backgroundColor: enabled ? '#262626' : '#1a1a1a',
  border: enabled ? '1px solid #3c3c3c' : '1px solid #282828',
  borderRadius: '10px',
  padding: '16px',
  opacity: enabled ? 1 : 0.65,
  transition: 'all 0.2s'
});

const badgeStyle = (enabled: boolean): React.CSSProperties => ({
  fontSize: '10px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '10px',
  backgroundColor: enabled ? 'rgba(74, 222, 128, 0.15)' : 'rgba(156, 163, 175, 0.15)',
  color: enabled ? '#4ade80' : '#9ca3af',
  border: enabled ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid rgba(156, 163, 175, 0.3)'
});

const toggleLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer'
};

const smallActionBtnStyle: React.CSSProperties = {
  backgroundColor: '#333',
  color: '#e5e7eb',
  border: '1px solid #444',
  borderRadius: '4px',
  padding: '4px 8px',
  fontSize: '11px',
  cursor: 'pointer'
};

const smallDeleteBtnStyle: React.CSSProperties = {
  backgroundColor: 'rgba(239, 68, 68, 0.15)',
  color: '#fca5a5',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  borderRadius: '4px',
  padding: '4px 8px',
  fontSize: '11px',
  cursor: 'pointer'
};

const editorColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column'
};

const editorCardStyle: React.CSSProperties = {
  backgroundColor: '#262626',
  border: '1px solid #333333',
  borderRadius: '10px',
  padding: '20px'
};

const placeholderCardStyle: React.CSSProperties = {
  backgroundColor: '#1a1a1a',
  border: '1px dashed #333333',
  borderRadius: '10px',
  padding: '40px 20px',
  textAlign: 'center',
  color: '#9ca3af'
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  color: '#9ca3af',
  fontWeight: 500,
  marginBottom: '6px'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#121212',
  border: '1px solid #333',
  borderRadius: '6px',
  padding: '8px 12px',
  color: '#f3f4f6',
  fontSize: '13px',
  boxSizing: 'border-box'
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#121212',
  border: '1px solid #333',
  borderRadius: '6px',
  padding: '10px 12px',
  color: '#f3f4f6',
  fontFamily: 'monospace',
  fontSize: '12px',
  lineHeight: '1.4',
  boxSizing: 'border-box',
  resize: 'vertical'
};

const saveBtnStyle: React.CSSProperties = {
  backgroundColor: '#9c27b0',
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer'
};

const cancelBtnStyle: React.CSSProperties = {
  backgroundColor: '#333333',
  color: '#e5e7eb',
  border: '1px solid #444',
  borderRadius: '6px',
  padding: '8px 14px',
  fontSize: '13px',
  cursor: 'pointer'
};

const errorAlertStyle: React.CSSProperties = {
  padding: '10px 14px',
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  borderRadius: '6px',
  color: '#fca5a5',
  fontSize: '13px',
  marginBottom: '16px'
};
